/**
 * Dialog de configuração do node Incorporar (Embed)
 * Permite configurar URL do conteúdo externo e altura do iframe
 * 
 * RELEASE: 1.0.0 - 2026-03-08 - Novo dialog de incorporação
 */

import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Code2, HelpCircle } from 'lucide-react';
import { FlowNode } from '@/types/flows';
import { NodeIdField, NodeIdFieldRef } from '@/components/tenant/flows/NodeIdField';
import { NodeHelpDialog } from './NodeHelpDialog';

interface ConfigureEmbedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode;
  onSave: (nodeId: string, config: Record<string, any>) => void;
}

/**
 * Dialog para configurar URL e altura do iframe de incorporação
 */
export function ConfigureEmbedDialog({ open, onOpenChange, node, onSave }: ConfigureEmbedDialogProps) {
  const config = node.data.config || {};
  const [url, setUrl] = useState<string>((config.url as string) || '');
  const [height, setHeight] = useState<number>((config.height as number) || 400);
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  /** Controle do modal de ajuda */
  const [showHelp, setShowHelp] = useState(false);

  /** Salva configuração do node */
  const handleSave = () => {
    onSave(node.id, {
      ...config,
      url: url.trim(),
      height,
      nickname: nicknameRef.current?.getNickname(),
    });
    onOpenChange(false);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Code2 className="h-5 w-5 text-sky-600" />
              Incorporar
            </DialogTitle>
            <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
              <HelpCircle className="h-5 w-5" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* ID do Node + Apelido */}
          <NodeIdField
            ref={nicknameRef}
            nodeId={node.id}
            defaultNickname={config.nickname as string}
          />

          {/* URL do conteúdo */}
          <div className="space-y-2">
            <Label>URL *</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.youtube.com/embed/..."
            />
            <p className="text-xs text-muted-foreground">
              URL do site, vídeo ou arquivo a incorporar. Suporta variáveis <code>{'{{variavel}}'}</code>
            </p>
            <p className="text-xs text-muted-foreground/70">
              💡 Para YouTube e Vimeo, cole a URL normal. A conversão para embed é automática.
            </p>
          </div>

          {/* Altura do iframe */}
          <div className="space-y-2">
            <Label>Altura (px)</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={height}
                onChange={(e) => setHeight(Math.max(100, parseInt(e.target.value) || 400))}
                min={100}
                max={1200}
                className="w-32"
              />
              <span className="text-sm text-muted-foreground">px</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Altura do conteúdo incorporado (mín: 100px, máx: 1200px)
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={!url.trim()}>Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <NodeHelpDialog open={showHelp} onOpenChange={setShowHelp} nodeType="embed" />
    </>
  );
}
